import type { LeapRefColumn } from '@dstbfs/ui-shell'
import { t } from '@/i18n/catalog'
/**
 * 存货档案（ADR-071 D2/D3）的**前端契约**：类型与路径都照 ADR-071 D3 写，S02 后端合入后以这里为对齐基准。
 *
 * - 协议字段名 `skuId` 在单据行 / 库存 / 打印快照上**不改**（ADR-071 D3、ADR-058：稳定 code）；本模块只管存货档案本身与 picker。
 * - 用户可见文案走 `dstbfs.web.inventory.*` 资源键，这里只有协议名。
 * - Verification：`test/e2e/{inventory-list,master-inventory-item-edit,so-editor,po-editor,pr-editor,quick-sell,doc-sku}.spec.ts`
 *   以 Playwright route mock 按本契约打桩；真后端复验在 S02 合入后进行（见 Evidence README）。
 */

/** `GET /api/v1/inventory-items/picker?q=&limit=` 的一行（ADR-071 D3）。**没有 `spuName`**——存货名就是 `name`。 */
export interface InventoryItemPickerRow {
  defaultPackaging?: import('./defaultPackaging').DefaultPackaging | null
  /** 存货 id（＝原 `md_sku.id`，ADR-071 D1）。单据行提交时填进 `skuId`。 */
  id: string
  code: string
  name: string
  /** 规格（O15-KR15.6 S11 起显示名「规格」：其他规格自由文本）。 */
  specification: string | null
  /** 型号：款号或系列。 */
  model: string | null
  /** 颜色（S11）：色号 + 颜色名，如「01白色」。旧后端没有这个键时为 undefined。 */
  color?: string | null
  /** 尺寸（S11）：原样文本，如「卡16」。 */
  size?: string | null
  /** 「规格型号」展示串（S11）：服务端拼好的 `型号 颜色 尺寸 规格`（跳过空值、空格连接；全空 null）。前端不自己拼。 */
  specModel?: string | null
  /** 主计量单位编码。 */
  baseUnitCode: string
  /** 主计量单位名称；服务端联 `md_unit` 得到，单位缺档时为 null。 */
  unitName: string | null
  salePrice: number | null
  overview?: string | null
  printOverview?: string | null
  supplierCode?: string | null
  supplierName?: string | null
  supplierSpec?: string | null
}

/** 存货 picker 路径（ADR-071 D3；原 `/api/v1/skus/picker`）。 */
export const INVENTORY_PICKER_PATH = '/api/v1/inventory-items/picker'

/** 拼 picker 请求路径：`limit` 固定带，`q` 非空才带。 */
export function pickerPath(q: string, limit = 30): string {
  return `${INVENTORY_PICKER_PATH}?limit=${limit}${q ? `&q=${encodeURIComponent(q)}` : ''}`
}

/** 采购价解析（ADR-071 D3：原 `/skus/{skuId}/purchase-price` 改为 `/inventory-items/{id}/purchase-price`）。 */
export function purchasePricePath(id: string): string {
  return `/api/v1/inventory-items/${encodeURIComponent(id)}/purchase-price`
}

/** 销售价解析；`customerId` 非空时带上，服务端据此优先返回该客户上次成交价。 */
export function salePricePath(id: string, customerId?: string): string {
  return `/api/v1/inventory-items/${encodeURIComponent(id)}/sale-price${customerId ? `?customerId=${encodeURIComponent(customerId)}` : ''}`
}

/** Description-only input shared by master rows, document lines and report read models. */
export interface ItemDisplaySource {
  overview?: unknown; printOverview?: unknown; sku_overview?: unknown; sku_print_overview?: unknown
  itemOverview?: unknown; itemSummary?: unknown; summary?: unknown; name?: unknown; ownName?: unknown
  effects?: unknown; sku_name?: unknown; skuName?: unknown; itemName?: unknown; details?: Record<string, unknown>
}

/**
 * The sole inventory description renderer. Server overview values are returned verbatim (apart from outer whitespace).
 * No item code, ID or client-assembled model/color/size/specification is ever a fallback.
 * Printing prefers printOverview; legacy/missing overview falls back to one name, then a localized unavailable label.
 * @param row A master row, picker row, report row or document line.
 * @param printing Select the server's print rules instead of screen rules.
 * @param missing Label for a missing description; callers may use an empty label for an unselected draft.
 * @returns A description without an inventory code prefix.
 */
export function itemOverviewText(row?: ItemDisplaySource | null, printing = false, missing = t('dstbfs.web.trade.unavailable')): string {
  const text = (value: unknown): string => typeof value === 'string' ? value.trim() : ''
  const screen = [row?.overview, row?.sku_overview, row?.itemOverview, row?.itemSummary, row?.summary, row?.details?.['overview']]
  const values = printing ? [row?.printOverview, row?.sku_print_overview, row?.details?.['printOverview'], ...screen] : screen
  for (const value of [...values, row?.ownName, row?.name, row?.sku_name, row?.skuName, row?.itemName]) {
    const description = text(value)
    if (description) return description
  }
  return missing
}

/** Shared description-only columns for inventory references that do not need unit metadata. */
export const INVENTORY_DESCRIPTION_COLUMNS: readonly LeapRefColumn[] = [{ field: 'name', titleKey: 'dstbfs.web.field.sku_overview', width: 360 }]

/** Picker labels use the same server overview as every inventory column. */
export function pickerLabel(r: InventoryItemPickerRow): string { return itemOverviewText(r) }

/** Compatibility entry point for existing editors; never assembles inventory fields. */
export function modelSpec(r: Pick<InventoryItemPickerRow, 'model' | 'specification' | 'specModel'> & ItemDisplaySource): string {
  return itemOverviewText(r, false, '')
}

/** Shared legacy response fallback; resolved effect names always come from the tenant dictionary. */
export function appendEffects(summary: string, effects?: string | null): string {
  return effects ? [summary, effects].filter(Boolean).join(',') : summary
}

/** Exact code membership, matching server export filtering. */
export function hasEffect(attributes: Record<string, unknown>, code: unknown): boolean {
  return Array.isArray(attributes['effects']) && attributes['effects'].includes(code)
}

/**
 * 保质期单位（ADR-071 D2 `expired_unit`）。线上是**枚举名字符串**（S01 `ExpiryUnit`：Day=1 / Month=2，读模型 `ToString()`、写入 `Enum.TryParse`），
 * 不是数字——稳定 code，不翻译（ADR-058）。
 */
export type ExpiredUnit = 'Day' | 'Month'
/** 计价方法（ADR-071 D2 `valuation_method`）：同上为枚举名（S01 `ValuationMethod`：MovingAverage=1 / MonthlyAverage=2 / Fifo=3 / SpecificIdentification=4）。 */
export type ValuationMethod = 'MovingAverage' | 'MonthlyAverage' | 'Fifo' | 'SpecificIdentification'
export const VALUATION_METHODS: readonly ValuationMethod[] = ['MovingAverage', 'MonthlyAverage', 'Fifo', 'SpecificIdentification']
export const EXPIRED_UNITS: readonly ExpiredUnit[] = ['Day', 'Month']

/**
 * `GET /api/v1/master/inventory-items` 每行 `details` 的字段集（ADR-071 D2，camelCase）。
 * 读模型的 `code`/`name`/`status`/`version` 在 `MasterView` 顶层，不在这里。
 * 图片（O15-KR15.6 S10）不在档案字段里：走 `…/{id}/images`；列表读模型另带只读的 `defaultImageId` / `defaultThumbUrl`（见 `defaultThumbOf`）。
 * 可空字段服务端给 null；前端表单里空串 ⇄ null 的转换在卡片里做。
 */
export interface InventoryItemDetails {
  // 基本（O15-KR15.6 S11：描述四列 型号 · 颜色 · 尺寸 · 规格）
  specification: string | null
  model: string | null
  color: string | null
  size: string | null
  shorthand: string | null
  categoryId: string | null
  brandId: string | null
  defaultSupplierId: string | null
  productNo: string | null
  alias: string | null
  barCode: string | null
  description: string | null
  weightKg: number | null
  volumeM3: number | null
  produceArea: string | null
  // 属性位（至少一位为真；劳务与其余互斥，且不得批次/序列号管理）
  isPurchase: boolean
  isSale: boolean
  isLaborCost: boolean
  isSuite: boolean
  // 批次 / 效期 / 序列号
  isBatch: boolean
  isQualityPeriod: boolean
  /** O15-KR15.12（ADR-081 D7）：需检验——默认 false；开启后收货进入待检，否则直接可用。 */
  requiresInspection?: boolean
  expired: number | null
  expiredUnit: ExpiredUnit | null
  isSerialNumber: boolean
  // 计量（单位组与换算行由 D4 的 `/units` 端点维护，S03 只读显示 `unitGroupId`）
  baseUnitCode: string
  unitGroupId: string | null
  purchaseUnitCode: string | null
  saleUnitCode: string | null
  stockUnitCode: string | null
  // 价格 / 税 / 成本
  purchasePrice: number | null
  salePrice: number | null
  retailPrice: number | null
  lowestSalePrice: number | null
  highestPurchasePrice: number | null
  referenceCost: number | null
  valuationMethod: ValuationMethod | null
  inputTaxRate: number | null
  outputTaxRate: number | null
  // 库存控制（low ≤ safe ≤ top）
  lowQuantity: number | null
  safeQuantity: number | null
  topQuantity: number | null
  inStorageUpLimitPct: number | null
  outStorageUpLimitPct: number | null
  purchaseBatch: number | null
  purchaseMinBatch: number | null
  purchaseLeadDays: number | null
  // 自定义项（T+ 固定 5 文本 + 5 数值）
  userDefText1: string | null
  userDefText2: string | null
  userDefText3: string | null
  userDefText4: string | null
  userDefText5: string | null
  userDefNum1: number | null
  userDefNum2: number | null
  userDefNum3: number | null
  userDefNum4: number | null
  userDefNum5: number | null
  // 扩展属性（KR2.4 类型化注册表，保留不动）
  attributes: Record<string, unknown>
}

/** `POST /api/v1/master/inventory-items` 请求体：单层一次带齐全部 D2 字段（S01 `BuildInventoryItemAsync` 逐键解析；`unitGroupId` 不在建档体里，单位组走 D4 的 `/units`）。 */
/** 建档体；`codeAuto: true`（O15-KR15.9 S03，ADR-079 D5）= 编码按分类规则由服务端在建档事务里生成并占号，请求里的 code 只是建议值。 */
export type InventoryItemCreateBody = { code: string; name: string; codeAuto?: boolean } & Omit<InventoryItemDetails, 'unitGroupId'>

/**
 * `PUT /api/v1/master/inventory-items/{id}` 请求体（S02，已合 main `3b43e8b`）：If-Match 必带（缺 428、不符 409 `conflict`）；扁平，键名与建档 / GET details 相同。
 * **未带的键保留、显式 null 清空**；可改 code（撞唯一 409 `code_taken`）与名称及 D2 其余字段。
 * 不带：`baseUnitCode`、`unitGroupId`（不可经此改，给了不同值 400）、`status`（走 `/status`），以及三个常用单位——
 * 常用单位必须落在本存货的单位行里，卡片把它们与单位组、单位行一起走 `PUT …/units`（见 `InventoryItemUnitsBody`），这里不重复带。
 * 卡片总是带齐其余全部字段（整条），服务端无变化时版本不涨。
 */
export type InventoryItemUpdateBody = { code: string; name: string } & Omit<InventoryItemDetails, 'unitGroupId' | 'baseUnitCode' | 'purchaseUnitCode' | 'saleUnitCode' | 'stockUnitCode'>

/** 修改模式下只读、改动要去别的入口的字段（主计量单位随单位组定，见计量单位页签）。 */
export const FIELDS_READONLY_ON_EDIT: readonly string[] = ['baseUnitCode']

/** 存货×单位行（ADR-071 D4，`md_inventory_item_unit`）：`rateOfExchange` = 1 个该单位折合多少主单位；主单位行恒为 1；固定换算必填、浮动可空。 */
export interface InventoryItemUnitRow {
  unitCode: string
  rateOfExchange: number | null
  purchasePrice: number | null
  salePrice: number | null
  retailPrice: number | null
  remark: string | null
  sortCode: number
}
/** `GET /api/v1/master/inventory-items/{id}/units` 响应（S02 `InventoryItemUnitsView`）。 */
export interface InventoryItemUnitsView {
  id: string
  version: number
  baseUnitCode: string
  unitGroupId: string | null
  purchaseUnitCode: string
  saleUnitCode: string
  stockUnitCode: string
  units: InventoryItemUnitRow[]
}
/**
 * `PUT /api/v1/master/inventory-items/{id}/units` 请求体（S02）：If-Match；整体设定。
 * 单计量（`unitGroupId` null）恰一行主单位；多计量每个组成员一行（不多不少），组主单位须等于存货主单位；三个常用单位须是行里的单位。
 * 与现状完全相同时版本不涨（可安全重放）。
 */
export interface InventoryItemUnitsBody {
  unitGroupId: string | null
  purchaseUnitCode: string
  saleUnitCode: string
  stockUnitCode: string
  units: InventoryItemUnitRow[]
}
/** 单位组成员（通用 `/master/unit-groups` 读模型 `details.members`）。 */
export interface UnitGroupMember { unitCode: string; isMain: boolean; changeType: 'Fixed' | 'Floating' | null; changeRate: number | null; sortCode: number }
export function membersOf(details: Record<string, unknown>): UnitGroupMember[] {
  const raw = details['members']
  if (!Array.isArray(raw)) { return [] }
  return raw.map((m) => {
    const x = m as Record<string, unknown>
    const ct = x['changeType']
    return { unitCode: String(x['unitCode'] ?? ''), isMain: x['isMain'] === true, changeType: ct === 'Fixed' || ct === 'Floating' ? ct : null,
      changeRate: typeof x['changeRate'] === 'number' ? x['changeRate'] : null, sortCode: typeof x['sortCode'] === 'number' ? x['sortCode'] : 0 }
  })
}

/** `GET /api/v1/master/inventory-items/import-template` 响应：列 code 即 CSV 表头（稳定不翻译），`label` 按请求语言。 */
export interface ImportTemplate { locale: string; columns: { code: string; label: string; required: boolean; type: string }[] }
/**
 * `POST /api/v1/master/inventory-items/import-file` 响应（`ImportReport`）。`outcome`/`reason` 是稳定 code（outcome ∈ created|updated|unchanged|failed，小写）；
 * O15-KR15.6 S12 起每行另带服务端按请求语言翻好的 `outcomeText` / `reasonText`——界面只显示这两个，不显示英文原文。
 */
export interface ImportReport { batchId: string; batchKey: string; kind: string; created: number; updated: number; failed: number; rows: ImportReportRow[]; replayed: boolean }
export interface ImportReportRow { row: number; code: string; outcome: string; reason: string | null; outcomeText?: string; reasonText?: string | null }
/** `POST …/import-preview` 响应（S12）：出现过的列（code + 当前语言列名 + 是否认识）、前 20 行原值、总行数、整批预演报告（预演后服务端回滚，什么都没写）。 */
export interface ImportPreview { columns: { code: string; label: string; known: boolean }[]; sample: Record<string, string | null>[]; totalRows: number; report: ImportReport }

/**
 * 网格列（`card.ts#columnScheme` 的 field）→ 导出列 code（= 导入列 code，S12）。名称列在导出里写编码（分类/品牌编码、单位编码），这样导出文件能原样再导入；
 * 缩略图、版本号没有对应的导出列（null，不导）。
 */
export const EXPORT_CODE_OF_FIELD: Readonly<Record<string, string | null>> = {
  code: 'code', name: 'name', model: 'model', color: 'color', effects: 'effects', size: 'size', specification: 'specification', unitName: 'baseUnitCode',
  categoryName: 'categoryCode', brandName: 'brandCode', isPurchase: 'isPurchase', isSale: 'isSale', isBatch: 'isBatch',
  purchasePrice: 'purchasePrice', salePrice: 'salePrice', status: 'status', thumbUrl: null, version: null, overview: null, // overview：服务端生成，只读，不导出（O15-KR15.11 S04）
}

/**
 * 价格可见性（O15-KR15.11 S02，ADR-080 D1）：`GET /api/v1/master/inventory-items/price-visibility` → `{viewCost, viewFinance}`。
 * 服务端对所有存货读接口按同一口径**去键**（安全边界在服务端）；前端据此先把列、卡片字段、计量单位价格列、售价区间查找藏起来，
 * 整条保存也不发看不见的键（服务端反正忽略）。售价始终可见；成本只有显式 `true` 才显示，加载/失败保持隐藏。
 */
export interface PriceVisibility { viewCost: boolean; viewFinance: boolean }
export const PRICE_VISIBILITY_PATH = '/api/v1/master/inventory-items/price-visibility'
/** 需要 ViewCost 的价格键（进价、参考成本、最高进价）。 */
export const COST_PRICE_FIELDS = ['purchasePrice', 'referenceCost', 'highestPurchasePrice'] as const
/** 所有成员可见的售价键（售价、零售价、最低售价）。 */
export const FINANCE_PRICE_FIELDS = ['salePrice', 'retailPrice', 'lowestSalePrice'] as const
/** 本主体看不见的价格键集合。 */
export function hiddenPriceFields(v: PriceVisibility): Set<string> {
  return new Set<string>([...(v.viewCost ? [] : COST_PRICE_FIELDS)])
}
/** 服务端回包 → 可见性：售价恒 true；成本只有显式 true 才可见。 */
export function priceVisibilityOf(raw: unknown): PriceVisibility {
  const o = (raw ?? {}) as Record<string, unknown>
  return { viewCost: o['viewCost'] === true, viewFinance: true }
}
/** 从对象里去掉看不见的价格键（返回新对象）。 */
export function withoutHidden<T extends object>(body: T, hidden: Set<string>): T {
  return Object.fromEntries(Object.entries(body).filter(([k]) => !hidden.has(k))) as T
}

/** 列表当前条件（左树 + 查找）→ 导出查询串。语义同页面 `matches()`；树选「全部」不带，选「未分类」带 uncategorized。 */
export function exportQuery(opts: { all: boolean; columns: string[]; categoryId: string | null; uncategorized: boolean; search: Record<string, unknown> }): string {
  const q = new URLSearchParams()
  if (opts.all) { q.set('all', 'true') } else { q.set('columns', opts.columns.join(',')) }
  if (opts.categoryId) { q.set('categoryId', opts.categoryId) }
  if (opts.uncategorized) { q.set('uncategorized', 'true') }
  for (const [k, v] of Object.entries(opts.search)) {
    if (v === undefined || v === null || v === '') { continue }
    if (k === 'salePrice' && Array.isArray(v)) {
      if (v[0] !== '' && v[0] !== undefined) { q.set('salePriceFrom', String(v[0])) }
      if (v[1] !== '' && v[1] !== undefined) { q.set('salePriceTo', String(v[1])) }
      continue
    }
    q.set(k, String(v))
  }
  return q.toString()
}

/** 把读模型 `details`（`Record<string, unknown>`）收窄成 `InventoryItemDetails`，缺字段按类型给默认值；不猜测未知字段。 */
export function detailsOf(d: Record<string, unknown>): InventoryItemDetails {
  const str = (k: string): string | null => { const v = d[k]; return typeof v === 'string' && v !== '' ? v : null }
  const num = (k: string): number | null => { const v = d[k]; return typeof v === 'number' ? v : typeof v === 'string' && v !== '' && Number.isFinite(Number(v)) ? Number(v) : null }
  const bool = (k: string, fallback = false): boolean => (typeof d[k] === 'boolean' ? (d[k] as boolean) : fallback)
  const expiredUnit = str('expiredUnit'); const valuation = str('valuationMethod')
  const attrs = d['attributes']
  return {
    specification: str('specification'), model: str('model'), color: str('color'), size: str('size'), shorthand: str('shorthand'),
    categoryId: str('categoryId'), brandId: str('brandId'), defaultSupplierId: str('defaultSupplierId'),
    productNo: str('productNo'), alias: str('alias'), barCode: str('barCode'),
    description: str('description'), weightKg: num('weightKg'), volumeM3: num('volumeM3'), produceArea: str('produceArea'),
    isPurchase: bool('isPurchase'), isSale: bool('isSale'), isLaborCost: bool('isLaborCost'), isSuite: bool('isSuite'),
    isBatch: bool('isBatch'), isQualityPeriod: bool('isQualityPeriod'), requiresInspection: bool('requiresInspection'), expired: num('expired'),
    expiredUnit: (EXPIRED_UNITS as readonly string[]).includes(expiredUnit ?? '') ? (expiredUnit as ExpiredUnit) : null, isSerialNumber: bool('isSerialNumber'),
    baseUnitCode: str('baseUnitCode') ?? '', unitGroupId: str('unitGroupId'),
    purchaseUnitCode: str('purchaseUnitCode'), saleUnitCode: str('saleUnitCode'), stockUnitCode: str('stockUnitCode'),
    purchasePrice: num('purchasePrice'), salePrice: num('salePrice'), retailPrice: num('retailPrice'),
    lowestSalePrice: num('lowestSalePrice'), highestPurchasePrice: num('highestPurchasePrice'), referenceCost: num('referenceCost'),
    valuationMethod: (VALUATION_METHODS as readonly string[]).includes(valuation ?? '') ? (valuation as ValuationMethod) : null,
    inputTaxRate: num('inputTaxRate'), outputTaxRate: num('outputTaxRate'),
    lowQuantity: num('lowQuantity'), safeQuantity: num('safeQuantity'), topQuantity: num('topQuantity'),
    inStorageUpLimitPct: num('inStorageUpLimitPct'), outStorageUpLimitPct: num('outStorageUpLimitPct'),
    purchaseBatch: num('purchaseBatch'), purchaseMinBatch: num('purchaseMinBatch'), purchaseLeadDays: num('purchaseLeadDays'),
    userDefText1: str('userDefText1'), userDefText2: str('userDefText2'), userDefText3: str('userDefText3'), userDefText4: str('userDefText4'), userDefText5: str('userDefText5'),
    userDefNum1: num('userDefNum1'), userDefNum2: num('userDefNum2'), userDefNum3: num('userDefNum3'), userDefNum4: num('userDefNum4'), userDefNum5: num('userDefNum5'),
    attributes: attrs !== null && typeof attrs === 'object' && !Array.isArray(attrs) ? (attrs as Record<string, unknown>) : {},
  }
}

/** 列表读模型上的默认图缩略图（O15-KR15.6 S10）：服务端现签的短时 URL（10 分钟），没有图为 null。只读，不进任何写请求体。 */
export function defaultThumbOf(d: Record<string, unknown>): string | null {
  const v = d['defaultThumbUrl']
  return typeof v === 'string' && v !== '' ? v : null
}
